"use client";

import { useEffect, useState, useRef } from "react";
import { cn } from "@/lib/utils";
import { User, MapPin, Building2, Wand2 } from "lucide-react";

export function WikiDemo() {
  const [step, setStep] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    async function runAnimation() {
      const isCancelled = () => cancelledRef.current;

      // Reset
      setStep(0);
      await wait(500);
      if (isCancelled()) return;

      // Step 1: Cards pop in, text appears
      setStep(1);
      await wait(1500);
      if (isCancelled()) return;

      // Step 2: Lines draw out
      setStep(2);
      await wait(1500);
      if (isCancelled()) return;

      // Step 3: Pulse effect
      setStep(3);
      await wait(5000);
      if (isCancelled()) return;

      // Loop
      if (!isCancelled()) {
        runAnimation();
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect(); // Only trigger once
          runAnimation();
        }
      },
      { threshold: 0.5 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      cancelledRef.current = true;
      observer.disconnect();
    };
  }, []);

  return (
    <section 
      ref={containerRef}
      className="mx-auto w-full max-w-6xl px-6 pt-24 pb-12 lg:px-12 flex flex-col md:flex-row gap-12 items-center justify-between"
    >
      {/* Visual Canvas Demo Container */}
      <div className="relative w-full max-w-2xl flex-1 rounded-3xl border border-white/10 bg-gradient-to-br from-indigo-900/20 via-background to-purple-900/10 shadow-2xl p-4 overflow-hidden before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] before:from-indigo-500/10 before:via-transparent before:to-transparent before:pointer-events-none">
        
        {/* Subtle grid background */}
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-[size:20px_20px] opacity-10" />

        <div className="relative w-full aspect-[4/3]">
          <svg viewBox="0 0 800 600" className="w-full h-full overflow-visible drop-shadow-lg">
            
            {/* DEFINITIONS */}
            <defs>
              <linearGradient id="gradientLine" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#818cf8" /> {/* text-indigo-400 */}
                <stop offset="100%" stopColor="#c084fc" /> {/* text-purple-400 */}
              </linearGradient>
              <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

            {/* CONNECTION LINES */}
            {/* To Iloris */}
            <path
              d="M 310 300 C 450 300, 420 160, 520 160"
              fill="none"
              stroke="url(#gradientLine)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="400"
              strokeDashoffset={step >= 2 ? "0" : "400"}
              className="transition-[stroke-dashoffset] duration-[1500ms] ease-in-out"
              filter="url(#glow)"
            />
            {/* To Rock Haven */}
            <path
              d="M 310 300 C 380 285, 450 315, 520 300"
              fill="none"
              stroke="url(#gradientLine)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="400"
              strokeDashoffset={step >= 2 ? "0" : "400"}
              className="transition-[stroke-dashoffset] duration-[1500ms] ease-in-out delay-200"
              filter="url(#glow)"
            />
            {/* To Fire Spirit */}
            <path
              d="M 310 300 C 450 300, 420 440, 520 440"
              fill="none"
              stroke="url(#gradientLine)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="400"
              strokeDashoffset={step >= 2 ? "0" : "400"}
              className="transition-[stroke-dashoffset] duration-[1500ms] ease-in-out delay-500"
              filter="url(#glow)"
            />

            {/* NODES via foreignObject */}
            
            {/* SOURCE NODE: Arthur */}
            <foreignObject x="60" y="240" width="250" height="120" className="overflow-visible">
              <div 
                className={cn(
                  "relative flex flex-col gap-2 rounded-2xl border border-indigo-500/30 bg-background/90 p-5 shadow-[0_0_30px_rgba(99,102,241,0.2)] backdrop-blur-xl transition-all duration-700 hover:-translate-y-1 hover:shadow-[0_8px_40px_rgba(99,102,241,0.3)]",
                  step >= 3 && "ring-2 ring-indigo-500/50 ring-offset-2 ring-offset-background"
                )}
              >
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-400">
                    <User size={18} />
                  </div>
                  <span className="text-xs font-bold tracking-wider text-indigo-400 uppercase">Character</span>
                </div>
                <div className="text-xl font-bold tracking-tight text-foreground">Arthur</div>
                
                {/* Connecting Dot */}
                <div className="absolute right-[-6px] top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-indigo-400 shadow-[0_0_10px_rgba(129,140,248,0.8)]" />
              </div>
            </foreignObject>

            {/* DESTINATION NODE 1: Iloris */}
            <foreignObject x="520" y="100" width="220" height="120" className="overflow-visible">
              <div 
                className={cn(
                  "relative flex flex-col gap-2 rounded-2xl border border-purple-500/30 bg-background/80 p-5 shadow-lg backdrop-blur-xl transition-all duration-700 hover:-translate-y-1",
                  step >= 1 ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
                )}
                style={{ transitionDelay: '0ms' }}
              >
                {/* Connecting Dot */}
                <div className="absolute left-[-6px] top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-purple-400 shadow-[0_0_10px_rgba(192,132,252,0.8)]" />
                
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-500/10 text-purple-400">
                    <MapPin size={18} />
                  </div>
                  <span className="text-xs font-bold tracking-wider text-purple-400 uppercase">Place</span>
                </div>
                <div className="text-lg font-bold tracking-tight text-foreground">Iloris</div>
              </div>
            </foreignObject>

            {/* DESTINATION NODE 2: Rock Haven */}
            <foreignObject x="520" y="240" width="220" height="120" className="overflow-visible">
              <div 
                className={cn(
                  "relative flex flex-col gap-2 rounded-2xl border border-emerald-500/30 bg-background/80 p-5 shadow-lg backdrop-blur-xl transition-all duration-700 hover:-translate-y-1",
                  step >= 1 ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
                )}
                style={{ transitionDelay: '300ms' }}
              >
                {/* Connecting Dot */}
                <div className="absolute left-[-6px] top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-purple-400 shadow-[0_0_10px_rgba(192,132,252,0.8)]" />
                
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-400">
                    <Building2 size={18} />
                  </div>
                  <span className="text-xs font-bold tracking-wider text-emerald-400 uppercase">Town</span>
                </div>
                <div className="text-lg font-bold tracking-tight text-foreground">Rock Haven</div>
              </div>
            </foreignObject>

             {/* DESTINATION NODE 3: Fire Spirit */}
             <foreignObject x="520" y="380" width="220" height="120" className="overflow-visible">
              <div 
                className={cn(
                  "relative flex flex-col gap-2 rounded-2xl border border-rose-500/30 bg-background/80 p-5 shadow-lg backdrop-blur-xl transition-all duration-700 hover:-translate-y-1",
                  step >= 1 ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
                )}
                style={{ transitionDelay: '600ms' }}
              >
                {/* Connecting Dot */}
                <div className="absolute left-[-6px] top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-purple-400 shadow-[0_0_10px_rgba(192,132,252,0.8)]" />
                
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500/10 text-rose-400">
                    <Wand2 size={18} />
                  </div>
                  <span className="text-xs font-bold tracking-wider text-rose-400 uppercase">Magic</span>
                </div>
                <div className="text-lg font-bold tracking-tight text-foreground">Fire Spirit</div>
              </div>
            </foreignObject>

          </svg>
        </div>
      </div>

      {/* Text Explainer Section */}
      <div className="flex-1 max-w-lg space-y-6">
        <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
          Build rich, connected worlds
        </h2>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Bring your universe to life with the Wiki. Map out characters, magical artifacts, locations, and lore in an interconnected graph.
        </p>
        
        <ul className="space-y-4 mt-8">
          <li 
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 1 ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: '800ms' }}
          >
            <div className="mt-1 bg-indigo-500/20 text-indigo-400 p-1 rounded-full shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
            <div>
              <span className="font-semibold text-foreground">Never lose track:</span> Instantly see how characters relate to places and events.
            </div>
          </li>
          <li 
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 1 ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: '1000ms' }}
          >
            <div className="mt-1 bg-purple-500/20 text-purple-400 p-1 rounded-full shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
            <div>
              <span className="font-semibold text-foreground">Auto-linked insights:</span> As you write, entities are detected and highlighted.
            </div>
          </li>
          <li 
            className={cn(
              "flex items-start gap-3 transition-opacity duration-700",
              step >= 1 ? "opacity-100" : "opacity-0"
            )}
            style={{ transitionDelay: '1200ms' }}
          >
             <div className="mt-1 bg-rose-500/20 text-rose-400 p-1 rounded-full shrink-0">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
            <div>
              <span className="font-semibold text-foreground">Infinite scale:</span> Build intricate magic systems without messy notes.
            </div>
          </li>
        </ul>
      </div>

    </section>
  );
}
